import type { Auth, User } from "firebase/auth";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type * as api from "./api";
import { useTitle } from "./hooks";
import { areaTotals } from "./sizeHeat";
import { SizeFailure, SizeTable, useSizeAccounts } from "./SizeTable";
import { SizeAreaTotals, SizeTreemap } from "./SizeTreemap";
import { LONG_PAYBACK_LINE, MAP_MEANING, NO_SAVINGS, noQuickPayback, NOTE_TITLE, pickRows, readSavingsPick, resultText, savingNote, SIZE_COLUMNS, SIZE_MEANING, type SavingsPick, sizeRows, writeSavingsPick } from "./sizes";
import { type Sort, sortRows } from "./sort";
import { SortSelect, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

const MOST_MINUTES: Sort = { key: "minutes", dir: "desc" };

/** Analyze > Size savings: the treemap of the containers and a table of the chosen area or account (rule W-5). */
export function SizeSavings({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const { accounts, prices, failed, load } = useSizeAccounts(user, canView);
  const [pick, setPickState] = useState<SavingsPick>(() => readSavingsPick(location.search));
  const [picks, setPicks] = useState(0);
  const [back, setBack] = useState<{ tile: string | null } | null>(null);
  const line = useRef<HTMLParagraphElement>(null);
  const lineBox = useRef<HTMLDivElement>(null);
  const page = useRef<HTMLElement>(null);
  const totalsHead = useRef<HTMLHeadingElement>(null);
  const [chosenSort, setSort] = useSort("sort", SIZE_COLUMNS);
  const sort = chosenSort ?? MOST_MINUTES;
  useTitle(`Size savings · ${org.name}`);
  const setPick = useCallback((next: SavingsPick) => {
    setPickState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeSavingsPick(location.search, next)}${location.hash}`);
  }, []);
  const choose = useCallback(
    (next: SavingsPick) => {
      setPick(next);
      setPicks((count) => count + 1);
    },
    [setPick],
  );
  const clear = useCallback(() => {
    setBack({ tile: pick.account });
    setPick({ area: null, account: null });
  }, [pick.account, setPick]);
  const openTotals = useCallback(() => {
    totalsHead.current?.scrollIntoView({ block: "start" });
    totalsHead.current?.focus({ preventScroll: true });
  }, []);
  const rows = useMemo(() => sizeRows(accounts ?? []), [accounts]);
  const totals = useMemo(() => areaTotals(rows), [rows]);
  const hasSavings = useMemo(() => rows.some((row) => row.saving !== null), [rows]);
  const shown = useMemo(() => sortRows(pickRows(rows, pick), SIZE_COLUMNS, sort), [rows, pick, sort]);
  const picked = pick.area !== null || pick.account !== null;
  const account = rows.find((row) => row.account.id === pick.account)?.account;
  useEffect(() => {
    if (picks === 0) return;
    line.current?.scrollIntoView({ block: "nearest" });
    line.current?.focus({ preventScroll: true });
  }, [picks]);
  useLayoutEffect(() => {
    const box = lineBox.current;
    if (!box) return;
    const measure = () => page.current?.style.setProperty("--pick-line", `${box.getBoundingClientRect().height}px`);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(measure);
    watch.observe(box);
    return () => watch.disconnect();
  }, [hasSavings]);
  return (
    <main ref={page} className="wide" data-testid="size-savings-page">
      <h1 tabIndex={-1}>Size savings</h1>
      {canView === false ? (
        <p>Your role does not show the accounts.</p>
      ) : (
        <>
          {accounts === undefined && !failed && <Skeleton shape="treemap" />}
          {hasSavings && (
            <details className="size-note" data-testid="size-savings-note">
              <summary>{NOTE_TITLE}</summary>
              <p className="sub">
                {SIZE_MEANING} {savingNote(prices)} {MAP_MEANING}
              </p>
            </details>
          )}
          <SizeFailure failed={failed} auth={auth} onRetry={load} />
          {accounts && !hasSavings && <p data-testid="sizes-no-savings">{NO_SAVINGS}</p>}
          {hasSavings && <SizeTreemap rows={rows} pick={pick} onPick={choose} onClear={clear} onTotals={totals.length > 0 ? openTotals : null} back={back} />}
          {hasSavings && (
            <div className="size-pick-line" ref={lineBox}>
              {picked && (
                <p className="freshness" data-testid="size-pick" tabIndex={-1} ref={line}>
                  {resultText(account?.number ?? pick.area ?? "One account", shown, account !== undefined)} ·{" "}
                  <button type="button" className="link" data-testid="size-pick-clear" onClick={clear}>
                    Show all accounts
                  </button>
                </p>
              )}
            </div>
          )}
          {hasSavings && noQuickPayback(shown) && (
            <p className="freshness" data-testid="size-long-payback">
              {LONG_PAYBACK_LINE}
            </p>
          )}
          {hasSavings && shown.length === 0 && <p data-testid="sizes-no-match">No accounts match the choice.</p>}
          {hasSavings && shown.length > 0 && <SortSelect id="sizes-sort" columns={SIZE_COLUMNS} sort={sort} onSort={setSort} fallback="Most minutes" />}
          {hasSavings && shown.length > 0 && <SizeTable rows={shown} base={base} sort={sort} onSort={setSort} />}
          {hasSavings && <SizeAreaTotals totals={totals} head={totalsHead} onPick={choose} />}
        </>
      )}
    </main>
  );
}
